import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import apis from '@src/apis';
import { NOTIFICATION_TABS } from '@src/constants/notification';
import moment from 'moment';
import {
  actionTypes,
  fetchNotificationsSuccess,
  fetchNotificationsFailure,
  markReadSuccess,
  markAllReadSuccess,
  fetchUnreadStatusSuccess,
} from './actions';

const normalizeNotification = (item) => ({
  id: item.id,
  rawContent: item.content || {},
  sentAt: item.sendTime,
  isPinned: item.isPinned,
  readAt: item?.readAt,
});

function* fetchNotificationsSaga(action) {
  try {
    const registeredAt = yield select((state) => state.auth.user?.createdAt);
    const response = yield call(apis.notification.getNotifications, {
      groupCode: action.groupCode,
      cursor: action.cursor,
      registeredAt,
    });
    const notifications = (response.result?.notifications || []).map(
      normalizeNotification,
    );
    const pagination = response.result?.pagination || {};
    yield put(
      fetchNotificationsSuccess({
        groupCode: action.groupCode,
        data: notifications,
        hasMore: pagination.hasNextPage ?? false,
        nextCursor: pagination.nextCursor || '',
        isLoadMore: action.isLoadMore,
      }),
    );
  } catch (error) {
    yield put(fetchNotificationsFailure(action.groupCode));
  }
}

function* markReadSaga(action) {
  try {
    yield call(apis.notification.markNotificationRead, action.notificationId);
    yield put(markReadSuccess(action.notificationId));
  } catch (error) {
    // silent fail
  }
}

function* markAllReadSaga(action) {
  try {
    yield call(apis.notification.markAllNotificationsRead, action.groupCode);
    yield put(markAllReadSuccess(action.groupCode));
  } catch (error) {
    // silent fail
  }
}

function* fetchGroupsUnreadStatusSaga() {
  try {
    const registeredAt = yield select((state) => state.auth.user?.createdAt);
    const [groupReadStatusResponse, notificationResponses] = yield all([
      call(apis.notification.getGroupsReadStatus),
      all(
        NOTIFICATION_TABS.map(({ key }) =>
          call(apis.notification.getNotifications, {
            groupCode: key,
            registeredAt,
          }),
        ),
      ),
    ]);

    const groupCodeReadStatus =
      groupReadStatusResponse?.result?.groupCodeReadStatus || [];
    const groupReadStatusMap = groupCodeReadStatus.reduce((acc, item) => {
      if (!item.readAt) return acc;
      acc[item.groupCode] = item.readAt;
      return acc;
    }, {});

    const unreadStatus = {};
    const putEffects = notificationResponses.map((response, index) => {
      const groupCode = NOTIFICATION_TABS[index].key;
      const notifications = (response?.result?.notifications || []).map(
        normalizeNotification,
      );
      const lastSentTime = Math.max(
        ...notifications.map((n) => moment(n.sentAt).valueOf()),
      );

      const hasGroupReadAt = groupReadStatusMap[groupCode]
        ? moment(groupReadStatusMap[groupCode]).valueOf() >= lastSentTime
        : false;
      const pagination = response?.result?.pagination || {};

      unreadStatus[groupCode] = notifications.length > 0 && !hasGroupReadAt;

      return put(
        fetchNotificationsSuccess({
          groupCode,
          data: notifications,
          hasMore: pagination.hasNextPage ?? false,
          nextCursor: pagination.nextCursor || '',
          isLoadMore: false,
        }),
      );
    });

    const readAtMax = Math.max(
      ...Object.values(groupReadStatusMap)?.map((item) =>
        moment(item).valueOf(),
      ),
    );

    const notificationsWithMaxKey = notificationResponses?.flatMap(
      (data) => data?.result?.notifications,
    );

    const hasNewNotification =
      (notificationsWithMaxKey?.length > 0 &&
        !Object.values(groupReadStatusMap)?.length) ||
      (notificationsWithMaxKey?.length > 0 &&
        notificationsWithMaxKey?.some(
          (item) => moment(item.sendTime).valueOf() >= readAtMax,
        ));

    yield all(putEffects);
    yield put(fetchUnreadStatusSuccess(unreadStatus, hasNewNotification));
  } catch (error) {
    // silent fail
  }
}

function* notificationSagas() {
  yield takeLatest(actionTypes.FETCH_NOTIFICATIONS, fetchNotificationsSaga);
  yield takeLatest(
    actionTypes.FETCH_GROUPS_UNREAD_STATUS,
    fetchGroupsUnreadStatusSaga,
  );
  yield takeLatest(actionTypes.MARK_READ, markReadSaga);
  yield takeLatest(actionTypes.MARK_ALL_READ, markAllReadSaga);
}

export default notificationSagas;
